import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { NAV_GROUPS } from '../../constants/nav';
import type { Screen } from '../../types';

interface Props {
  forceCollapsed?: boolean;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const DEFAULT_OPEN: Record<string, boolean> = { library: true, demohub: false };

export default function Sidebar({ forceCollapsed, isMobile, isOpen, onClose }: Props) {
  const { screen, go, currentUser, logout, nudges, leads, playbooks, setLibraryTab } = useApp();
  const [userCollapsed, setUserCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(DEFAULT_OPEN);

  const isCollapsed = isMobile ? false : (userCollapsed || !!forceCollapsed);
  const toggleGroup = (id: string) => setOpenGroups(p => ({ ...p, [id]: !p[id] }));

  const hotLeads     = leads.filter(l => l.heat === 'hot' && l.status === 'new').length;
  const pendingNudges = nudges.filter(n => !n.sent).length;
  const playbookCount = playbooks.length;

  function getBadge(id: string): { text?: string; red?: boolean; green?: boolean } | null {
    if (id === 'leads')    return hotLeads > 0 ? { text: `${hotLeads}`, green: true } : null;
    if (id === 'nudges')   return pendingNudges > 0 ? { text: String(pendingNudges) } : null;
    if (id === 'playbooks') return { text: String(playbookCount) };
    if (id === 'tracking') return { text: '2' };
    return null;
  }

  function isActive(itemId: string, itemScreen?: string): boolean {
    if (itemId === 'collaterals' && screen === 'admin') return true;
    if (itemScreen) return screen === itemScreen;
    return false;
  }

  function navigate(itemScreen?: Screen, tab?: 'collaterals' | 'playbooks') {
    if (!itemScreen) return;
    if (tab) setLibraryTab(tab);
    go(itemScreen);
    onClose?.();
  }

  const sideW = isMobile ? '260px' : isCollapsed ? '56px' : '228px';

  return (
    <>
      {/* Backdrop (mobile) */}
      {isMobile && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 299,
            background: 'rgba(12,25,41,.65)',
            backdropFilter: 'blur(4px)',
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
            transition: 'opacity .22s',
          }}
        />
      )}

      <div style={{
        width: sideW,
        background: 'linear-gradient(180deg, #0D1E30 0%, #0C1929 60%, #0A1520 100%)',
        flexShrink: 0, display: 'flex', flexDirection: 'column',
        height: '100vh', overflow: 'hidden',
        transition: isMobile ? 'transform .25s cubic-bezier(.4,0,.2,1)' : 'width .2s cubic-bezier(.4,0,.2,1)',
        borderRight: '1px solid rgba(255,255,255,.06)',
        ...(isMobile ? {
          position: 'fixed' as const, left: 0, top: 0, bottom: 0, zIndex: 300,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          boxShadow: isOpen ? '8px 0 40px rgba(0,0,0,.45)' : 'none',
        } : {}),
      }}>

        {/* ── Brand row ── */}
        <div style={{
          padding: isCollapsed ? '16px 0 14px' : '16px 14px 14px',
          display: 'flex', alignItems: 'center', gap: '10px',
          borderBottom: '1px solid rgba(255,255,255,.06)',
          flexShrink: 0, justifyContent: isCollapsed ? 'center' : undefined,
        }}>
          {/* Logo mark */}
          <div style={{
            width: '32px', height: '32px', borderRadius: '9px', flexShrink: 0,
            background: 'linear-gradient(135deg, #D4A042, #EAB96A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '15px', fontWeight: '800', color: '#0C1929',
            boxShadow: '0 0 0 1px rgba(212,160,66,.4), 0 4px 12px rgba(212,160,66,.25)',
          }}>S</div>

          {!isCollapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#FFFFFF', letterSpacing: '-.1px' }}>SalesBook</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.38)', marginTop: '1px', fontWeight: '500' }}>
                  {currentUser?.title || 'effyBiz'}
                </div>
              </div>
              {isMobile ? (
                <button
                  onClick={onClose}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.35)', cursor: 'pointer', fontSize: '17px', flexShrink: 0, padding: '3px', lineHeight: 1, borderRadius: '6px', transition: 'color .15s' }}
                >
                  <i className="ti ti-x" />
                </button>
              ) : !forceCollapsed && (
                <button
                  onClick={() => setUserCollapsed(true)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.25)', cursor: 'pointer', fontSize: '14px', flexShrink: 0, padding: '3px', lineHeight: 1, borderRadius: '6px', transition: 'color .15s' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,.6)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,.25)')}
                >
                  <i className="ti ti-layout-sidebar-left-collapse" />
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Expand button (collapsed desktop) ── */}
        {isCollapsed && !forceCollapsed && (
          <button
            onClick={() => setUserCollapsed(false)}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '15px', padding: '11px 0', display: 'flex', justifyContent: 'center', flexShrink: 0, transition: 'color .15s' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,.75)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,.3)')}
          >
            <i className="ti ti-layout-sidebar-left-expand" />
          </button>
        )}

        {/* ── Live pulse banner ── */}
        {!isCollapsed && (
          <div style={{
            margin: '10px 10px 2px',
            padding: '9px 10px',
            background: 'rgba(34,197,94,.08)',
            border: '1px solid rgba(34,197,94,.2)',
            borderRadius: '10px',
            flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer',
            transition: 'background .15s',
          }}
            onClick={() => go('tracking')}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(34,197,94,.14)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(34,197,94,.08)')}
          >
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%',
              background: '#22C55E', flexShrink: 0,
              boxShadow: '0 0 0 3px rgba(34,197,94,.22)',
              animation: 'sidePulse 2s ease-in-out infinite',
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#4ADE80', lineHeight: 1.3 }}>Priya Sharma</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.42)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>on page 4 — Pricing</div>
            </div>
            <i className="ti ti-arrow-right" style={{ fontSize: '11px', color: 'rgba(255,255,255,.3)', flexShrink: 0 }} />
          </div>
        )}

        {/* ── Nav scroll area ── */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '6px 0 8px' }}>
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi}>
              {/* Group label */}
              {group.group && !isCollapsed && (
                <div style={{
                  fontSize: '9px', fontWeight: '700', textTransform: 'uppercase',
                  letterSpacing: '.12em', color: 'rgba(255,255,255,.22)',
                  padding: '14px 16px 5px', whiteSpace: 'nowrap',
                }}>
                  {group.group}
                </div>
              )}
              {group.group && isCollapsed && (
                <div style={{ height: '1px', background: 'rgba(255,255,255,.07)', margin: '8px 12px' }} />
              )}

              {group.items.map(item => {
                const dynamicBadge = getBadge(item.id);
                const badge = dynamicBadge || (item.badge ? { text: item.badge, red: item.badgeRed, green: item.badgeGreen } : null);
                const hasChildren = !!item.children?.length;
                const groupOpen = openGroups[item.id] ?? false;
                const parentActive = hasChildren
                  ? item.children!.some(c => c.screen === screen)
                  : screen === item.screen;

                return (
                  <div key={item.id}>
                    {/* Parent item */}
                    <button
                      title={isCollapsed ? item.label : undefined}
                      style={{
                        display: 'flex', alignItems: 'center',
                        gap: isCollapsed ? 0 : '9px',
                        margin: isCollapsed ? '1px 8px' : '1px 8px',
                        padding: isCollapsed ? '9px 0' : '8px 10px',
                        width: isCollapsed ? 'calc(100% - 16px)' : 'calc(100% - 16px)',
                        borderRadius: '9px',
                        cursor: 'pointer',
                        border: 'none',
                        background: parentActive && !hasChildren
                          ? 'rgba(212,160,66,.18)'
                          : 'transparent',
                        color: parentActive && !hasChildren
                          ? '#EAB96A'
                          : 'rgba(255,255,255,.6)',
                        fontSize: '12.5px',
                        fontWeight: parentActive && !hasChildren ? '600' : '500',
                        fontFamily: 'inherit',
                        textAlign: isCollapsed ? 'center' : 'left',
                        justifyContent: isCollapsed ? 'center' : undefined,
                        whiteSpace: 'nowrap',
                        transition: 'background .14s, color .14s',
                        position: 'relative',
                      }}
                      onClick={() => {
                        if (hasChildren) { if (!isCollapsed) toggleGroup(item.id); }
                        else navigate(item.screen as Screen | undefined);
                      }}
                      onMouseEnter={e => {
                        if (!(parentActive && !hasChildren)) {
                          e.currentTarget.style.background = 'rgba(255,255,255,.06)';
                          e.currentTarget.style.color = 'rgba(255,255,255,.85)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!(parentActive && !hasChildren)) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'rgba(255,255,255,.6)';
                        }
                      }}
                    >
                      {/* Gold dot indicator on active */}
                      {parentActive && !hasChildren && (
                        <span style={{
                          position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                          width: '3px', height: '18px',
                          background: 'linear-gradient(180deg, #D4A042, #EAB96A)',
                          borderRadius: '0 3px 3px 0',
                        }} />
                      )}
                      <i className={`ti ${item.icon}`} style={{
                        fontSize: '16px', flexShrink: 0,
                        width: isCollapsed ? '100%' : '18px',
                        textAlign: 'center',
                        color: parentActive && !hasChildren ? '#EAB96A' : 'inherit',
                      }} />
                      {!isCollapsed && (
                        <>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                          {badge && (
                            <span style={{
                              fontSize: '9px', fontWeight: '700', borderRadius: '20px',
                              padding: '2px 7px', flexShrink: 0,
                              background: badge.green
                                ? 'rgba(74,222,128,.18)'
                                : badge.red
                                  ? '#EF4444'
                                  : 'rgba(212,160,66,.22)',
                              color: badge.green ? '#4ADE80' : badge.red ? 'white' : '#EAB96A',
                              border: badge.green ? '1px solid rgba(74,222,128,.3)' : 'none',
                            }}>
                              {badge.text}
                            </span>
                          )}
                          {hasChildren && (
                            <i className={`ti ti-chevron-${groupOpen ? 'up' : 'down'}`}
                              style={{ fontSize: '11px', color: 'rgba(255,255,255,.25)', flexShrink: 0 }} />
                          )}
                        </>
                      )}
                    </button>

                    {/* Sub-items */}
                    {hasChildren && groupOpen && !isCollapsed && (
                      <div style={{ paddingBottom: '2px' }}>
                        {item.children!.map(child => {
                          const childActive = screen === child.screen;
                          const childBadge = getBadge(child.id) || (child.badge ? { text: child.badge } : null);
                          return (
                            <button
                              key={child.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                margin: '1px 8px 1px 20px',
                                padding: '7px 10px',
                                width: 'calc(100% - 28px)',
                                borderRadius: '8px',
                                cursor: 'pointer', border: 'none',
                                background: childActive ? 'rgba(212,160,66,.14)' : 'transparent',
                                color: childActive ? '#EAB96A' : 'rgba(255,255,255,.48)',
                                fontSize: '12px',
                                fontWeight: childActive ? '600' : '400',
                                fontFamily: 'inherit',
                                whiteSpace: 'nowrap',
                                transition: 'background .14s, color .14s',
                              }}
                              onClick={() => navigate(child.screen, child.tab)}
                              onMouseEnter={e => {
                                if (!childActive) { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; e.currentTarget.style.color = 'rgba(255,255,255,.75)'; }
                              }}
                              onMouseLeave={e => {
                                if (!childActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.48)'; }
                              }}
                            >
                              <i className={`ti ${child.icon}`} style={{ fontSize: '13px', flexShrink: 0, width: '14px', textAlign: 'center' }} />
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{child.label}</span>
                              {childBadge && (
                                <span style={{ fontSize: '9px', fontWeight: '700', borderRadius: '20px', padding: '1px 6px', flexShrink: 0, background: 'rgba(212,160,66,.2)', color: '#EAB96A' }}>
                                  {childBadge.text}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── User footer ── */}
        <div style={{
          padding: '8px 10px',
          borderTop: '1px solid rgba(255,255,255,.07)',
          flexShrink: 0,
        }}>
          <div
            onClick={logout}
            style={{
              display: 'flex', alignItems: 'center', gap: '9px',
              cursor: 'pointer', padding: '7px 8px',
              borderRadius: '10px',
              justifyContent: isCollapsed ? 'center' : undefined,
              transition: 'background .15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <div style={{
              width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
              background: currentUser?.color || '#D4A042',
              color: '#0C1929', fontSize: '11px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 2px rgba(255,255,255,.12)',
            }}>
              {currentUser?.ini}
            </div>
            {!isCollapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,.88)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentUser?.name}
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.32)', fontWeight: '500', textTransform: 'capitalize' }}>
                  {currentUser?.role}
                </div>
              </div>
            )}
            {!isCollapsed && (
              <i className="ti ti-logout" style={{ fontSize: '14px', color: 'rgba(255,255,255,.22)', flexShrink: 0, transition: 'color .15s' }} />
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sidePulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(34,197,94,.22); }
          50%       { box-shadow: 0 0 0 6px rgba(34,197,94,.08); }
        }
      `}</style>
    </>
  );
}
